import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { sendResponseEmail } from "../services/emailService";

const RESPONSES = {
  yes: {
    emoji: "💖",
    headline: "She said YES!",
    message:
      "You have no idea how long I've wanted to ask you this. This is the beginning of something so beautiful, Zainab. I promise to make every single day worth it. You're mine now — and I'm completely yours.",
    sub: "the answer I was hoping for",
    color: "#D4526E",
    confetti: true,
  },
  no: {
    emoji: "🥺",
    headline: "Okay… I respect that.",
    message:
      "I won't pretend that doesn't sting a little. But know that I mean every word on this page. You deserve the most genuine love there is — and I hope you find it, even if it's not with me. Thank you for letting me tell you how I feel.",
    sub: "still, no regrets",
    color: "#C9A96E",
    confetti: false,
  },
  think: {
    emoji: "🤍",
    headline: "Take all the time you need.",
    message:
      "There's no rush. What matters is that you know — clearly, completely, intentionally. I'm not going anywhere. When you're ready to talk, I'll be right here. No pressure. Just honesty.",
    sub: "i'll wait",
    color: "#F2A7A7",
    confetti: false,
  },
};

function Confetti() {
  const pieces = Array.from({ length: 70 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    color: ["#D4526E", "#F2A7A7", "#C9A96E", "#FDF6F0", "#FF8FAB", "#FFD700"][
      i % 6
    ],
    delay: Math.random() * 1.5,
    duration: Math.random() * 2 + 2.5,
    size: Math.random() * 9 + 4,
    rotate: Math.random() * 360,
    drift: (Math.random() - 0.5) * 250,
  }));
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 50,
        overflow: "hidden",
      }}
    >
      {pieces.map((p) => (
        <motion.div
          key={p.id}
          initial={{ y: -20, opacity: 1, rotate: 0, x: 0 }}
          animate={{
            y: "110vh",
            opacity: [1, 1, 0],
            rotate: p.rotate + 360,
            x: p.drift,
          }}
          transition={{ duration: p.duration, delay: p.delay, ease: "linear" }}
          style={{
            position: "absolute",
            left: p.left,
            top: 0,
            width: p.size,
            height: p.size * 0.5,
            background: p.color,
            borderRadius: 2,
          }}
        />
      ))}
    </div>
  );
}

export default function FinalPage() {
  const [answer, setAnswer] = useState(null);
  const [userMessage, setUserMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const response = answer ? RESPONSES[answer] : null;

  const handleSubmit = async () => {
    if (isSubmitting || isSent) return;
    setIsSubmitting(true);
    try {
      const result = await sendResponseEmail(answer, userMessage);
      if (result.success) {
        setIsSent(true);
      }
    } catch (error) {
      console.error("Submission failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "80px 24px",
      }}
    >
      {/* BG glow */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(ellipse 80% 70% at 50% 40%, rgba(212,82,110,0.22) 0%, rgba(160,41,74,0.1) 40%, transparent 70%)",
        }}
      />
      {/* Grid */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.04,
          backgroundImage:
            "linear-gradient(rgba(201,169,110,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(201,169,110,0.4) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {response && response.confetti && <Confetti />}

      <AnimatePresence mode="wait">
        {!answer ? (
          <motion.div
            key="question"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30, scale: 0.96 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            style={{
              textAlign: "center",
              maxWidth: 480,
              position: "relative",
              zIndex: 10,
            }}
          >
            {/* Circular photo pair */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 0,
                marginBottom: 40,
              }}
            >
              {/* Ari */}
              <motion.div
                initial={{ x: 30, opacity: 0, scale: 0.8 }}
                animate={{ x: 0, opacity: 1, scale: 1 }}
                transition={{
                  delay: 0.2,
                  duration: 0.7,
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "3px solid rgba(201,169,110,0.5)",
                  boxShadow: "0 0 40px rgba(212,82,110,0.3)",
                  position: "relative",
                  zIndex: 1,
                  marginRight: -20,
                }}
              >
                <img
                  src="/ari1.jpg"
                  alt="Ari"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "top center",
                  }}
                />
              </motion.div>
              {/* Heart center */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  delay: 0.45,
                  type: "spring",
                  stiffness: 240,
                  damping: 14,
                }}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg,#D4526E,#A0294A)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  zIndex: 3,
                  alignSelf: "center",
                  boxShadow: "0 4px 20px rgba(212,82,110,0.5)",
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: 20 }} className="heart-beat">
                  ♥
                </span>
              </motion.div>
              {/* Zainab */}
              <motion.div
                initial={{ x: -30, opacity: 0, scale: 0.8 }}
                animate={{ x: 0, opacity: 1, scale: 1 }}
                transition={{
                  delay: 0.3,
                  duration: 0.7,
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "3px solid rgba(242,167,167,0.5)",
                  boxShadow: "0 0 40px rgba(242,167,167,0.25)",
                  position: "relative",
                  zIndex: 1,
                  marginLeft: -20,
                }}
              >
                <img
                  src="/zainab1.jpg"
                  alt="Zainab"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "top center",
                  }}
                />
              </motion.div>
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              style={{
                fontFamily: "'Lato', sans-serif",
                fontSize: "0.62rem",
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: "#C9A96E",
                marginBottom: 10,
              }}
            >
              zainab abdulkareem
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.62 }}
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(2.1rem, 6vw, 3.8rem)",
                fontWeight: 500,
                lineHeight: 1.1,
                color: "#FDF6F0",
                marginBottom: 16,
              }}
            >
              Will you be my{" "}
              <span style={{ fontStyle: "italic", color: "#F2A7A7" }}>
                girlfriend?
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.75 }}
              style={{
                fontFamily: "'Lato', sans-serif",
                fontWeight: 300,
                fontSize: "0.9rem",
                color: "rgba(253,246,240,0.5)",
                lineHeight: 1.9,
                marginBottom: 44,
              }}
            >
              You've seen how I feel. You know it's real.
              <br />
              Now, it's entirely your call.
            </motion.p>

            {/* Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.88 }}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 14,
                alignItems: "center",
              }}
            >
              {/* YES - big, prominent */}
              <button
                onClick={() => setAnswer("yes")}
                style={{
                  padding: "16px 52px",
                  borderRadius: 99,
                  fontFamily: "'Lato', sans-serif",
                  fontSize: "0.82rem",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  background: "linear-gradient(135deg, #D4526E, #A0294A)",
                  color: "#FDF6F0",
                  border: "none",
                  cursor: "pointer",
                  boxShadow:
                    "0 10px 36px rgba(212,82,110,0.5), inset 0 1px 0 rgba(255,255,255,0.15)",
                  transition: "all 0.25s ease",
                  minWidth: 220,
                }}
              >
                Yes 💖
              </button>

              <div style={{ display: "flex", gap: 12 }}>
                <button
                  onClick={() => setAnswer("think")}
                  style={{
                    padding: "12px 24px",
                    borderRadius: 99,
                    fontFamily: "'Lato', sans-serif",
                    fontSize: "0.72rem",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    background: "transparent",
                    border: "1px solid rgba(201,169,110,0.45)",
                    color: "rgba(253,246,240,0.65)",
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                  }}
                >
                  I have to think about it…
                </button>
                <button
                  onClick={() => setAnswer("no")}
                  style={{
                    padding: "12px 22px",
                    borderRadius: 99,
                    fontFamily: "'Lato', sans-serif",
                    fontSize: "0.72rem",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    background: "transparent",
                    border: "1px solid rgba(253,246,240,0.12)",
                    color: "rgba(253,246,240,0.35)",
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                  }}
                >
                  No
                </button>
              </div>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="response"
            initial={{ opacity: 0, scale: 0.86, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            style={{
              textAlign: "center",
              maxWidth: 440,
              position: "relative",
              zIndex: 10,
            }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                delay: 0.1,
                type: "spring",
                stiffness: 220,
                damping: 14,
              }}
              style={{
                fontSize: "5rem",
                marginBottom: 20,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <span className={answer === "yes" ? "heart-beat" : ""}>
                {response.emoji}
              </span>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25 }}
              style={{
                fontFamily: "'Lato', sans-serif",
                fontSize: "0.62rem",
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: response.color,
                marginBottom: 8,
              }}
            >
              {response.sub}
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(1.7rem, 5vw, 2.6rem)",
                fontWeight: 500,
                color: "#FDF6F0",
                lineHeight: 1.2,
                marginBottom: 28,
              }}
            >
              {response.headline}
            </motion.h2>

            {!isSent ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                style={{
                  position: "relative",
                  padding: "32px 28px",
                  borderRadius: 24,
                  marginBottom: 28,
                  background: `linear-gradient(145deg, ${response.color}10, rgba(26,10,16,0.55))`,
                  border: `1px solid ${response.color}35`,
                  backdropFilter: "blur(10px)",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: 12,
                    left: 18,
                    fontSize: "3rem",
                    opacity: 0.15,
                    fontFamily: "'Cormorant Garamond', serif",
                    color: response.color,
                    lineHeight: 1,
                  }}
                >
                  "
                </span>
                <textarea
                  value={userMessage}
                  onChange={(e) => setUserMessage(e.target.value)}
                  placeholder="Write your heart out here... Ari is waiting to hear from you."
                  style={{
                    width: "100%",
                    minHeight: "180px",
                    background: "transparent",
                    border: "none",
                    color: "rgba(253,246,240,0.9)",
                    fontFamily: "'Lato', sans-serif",
                    fontWeight: 300,
                    fontSize: "0.95rem",
                    lineHeight: 1.65,
                    resize: "none",
                    outline: "none",
                    padding: 0,
                    margin: 0,
                  }}
                />
                <div
                  style={{
                    marginTop: 20,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !userMessage.trim()}
                    style={{
                      padding: "10px 24px",
                      borderRadius: 99,
                      fontFamily: "'Lato', sans-serif",
                      fontSize: "0.7rem",
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                      background: isSubmitting
                        ? "rgba(253,246,240,0.1)"
                        : response.color,
                      color: "#FDF6F0",
                      border: "none",
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      boxShadow: `0 4px 15px ${response.color}40`,
                      transition: "all 0.2s ease",
                      opacity: isSubmitting ? 0.7 : 1,
                    }}
                  >
                    {isSubmitting ? "Sending..." : "Send Response"}
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  marginBottom: 40,
                  padding: "0 20px",
                }}
              >
                <p
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontStyle: "italic",
                    fontSize: "1.3rem",
                    color: "rgba(253,246,240,0.9)",
                    lineHeight: 1.6,
                    margin: "0 0 16px 0",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  "{userMessage}"
                </p>
                <div
                  style={{
                    color: response.color,
                    fontSize: "0.75rem",
                    fontFamily: "'Lato'",
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: "1.2rem" }}>✓</span>
                  sent to ari
                </div>
              </motion.div>
            )}

            {/* Photo strip if YES */}
            {answer === "yes" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 8,
                  marginBottom: 24,
                }}
              >
                {["/ari2.jpg", "/zainab1.jpg", "/ari4.jpg", "/zainab4.jpg"].map(
                  (src, i) => (
                    <div
                      key={i}
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 12,
                        overflow: "hidden",
                        border: "1px solid rgba(201,169,110,0.3)",
                        transform: `rotate(${[-4, 3, -3, 5][i]}deg)`,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                      }}
                    >
                      <img
                        src={src}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          objectPosition: "top center",
                        }}
                      />
                    </div>
                  ),
                )}
              </motion.div>
            )}

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.75 }}
              style={{
                fontFamily: "'Dancing Script', cursive",
                fontSize: "1.5rem",
                color: "#C9A96E",
              }}
            >
              — ARI ♥
            </motion.p>

            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.0 }}
              onClick={() => {
                setAnswer(null);
                setIsSent(false);
                setUserMessage("");
              }}
              style={{
                marginTop: 28,
                fontSize: "0.65rem",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#FDF6F0",
                opacity: 0.25,
                fontFamily: "'Lato'",
                transition: "opacity 0.2s",
              }}
            >
              ← go back
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
